// utils/missionsApi.js
// Centralised helpers for the ProMeet Missions module.
// Re-uses the axios instance from src/api.js (auth token + baseURL).

import API, { BASE_URL } from "../api";

/* ============================================================
   Asset URL helper — turns "/uploads/xxx.jpg" into a full URL
============================================================ */
export function assetUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith("/")) return `${BASE_URL}${pathOrUrl}`;
  return `${BASE_URL}/${pathOrUrl}`;
}

/* ============================================================
   Catalogue (UI labels for the 14 mission types)
============================================================ */
export const MISSION_TYPES = [
  { key: "carrosserie_retouche",     label: "Retouche carrosserie",   group: "bodywork",  icon: "🎨" },
  { key: "carrosserie_petit_choc",   label: "Petit choc carrosserie", group: "bodywork",  icon: "🔧" },
  { key: "debosselage",              label: "Débosselage",            group: "bodywork",  icon: "🛠️" },
  { key: "reparation_jantes",        label: "Réparation jantes",      group: "wheels",    icon: "🛞" },
  { key: "renovation_jantes",        label: "Rénovation jantes",      group: "wheels",    icon: "✨" },
  { key: "peinture_jantes",          label: "Peinture jantes",        group: "wheels",    icon: "🎨" },
  { key: "nettoyage_interieur",      label: "Nettoyage intérieur",    group: "cleaning",  icon: "🧽" },
  { key: "nettoyage_exterieur",      label: "Nettoyage extérieur",    group: "cleaning",  icon: "🚿" },
  { key: "preparation_vehicule",     label: "Préparation véhicule",   group: "cleaning",  icon: "🚗" },
  { key: "polissage_lustrage",       label: "Polissage / Lustrage",   group: "cleaning",  icon: "💎" },
  { key: "convoyage",                label: "Convoyage véhicule",     group: "convoy",    icon: "🚛" },
  { key: "photos_avant_apres",       label: "Photos avant/après",     group: "media",     icon: "📸" },
  { key: "intervention_journee",     label: "Intervention 1 jour",    group: "duration",  icon: "📅" },
  { key: "intervention_multi_jours", label: "Intervention multi-jours", group: "duration", icon: "🗓️" },
];

export const URGENCY_LEVELS = [
  { key: "normal",       label: "Normal",       tone: "neutral" },
  { key: "urgent",       label: "Urgent",       tone: "warn" },
  { key: "tres_urgent",  label: "Très urgent",  tone: "danger" },
];

export const DURATION_TYPES = [
  { key: "heure",         label: "Quelques heures" },
  { key: "demi_journee",  label: "Demi-journée" },
  { key: "journee",       label: "Journée" },
  { key: "multi_jours",   label: "Multi-jours" },
];

export const AVAILABILITIES = [
  { key: "disponible_maintenant",   label: "Disponible maintenant" },
  { key: "disponible_aujourdhui",   label: "Disponible aujourd'hui" },
  { key: "disponible_demain",       label: "Disponible demain" },
  { key: "disponible_semaine",      label: "Cette semaine" },
  { key: "sur_devis",               label: "Sur devis" },
];

export const RADIUS_OPTIONS = [10, 30, 50, 100];

export const STATUS_LABELS = {
  en_attente_validation:  "En validation admin",
  refusee_admin:          "Refusée par admin",
  ouverte:                "Ouverte",
  propositions_recues:    "Propositions reçues",
  proposition_acceptee:   "Proposition acceptée",
  presence_confirmee:     "Présence confirmée",
  en_cours:               "En cours",
  terminee:               "Terminée",
  validee:                "Validée",
  annulee:                "Annulée",
  litige:                 "Litige",
};

export const STATUS_TONES = {
  en_attente_validation:  "warn",
  refusee_admin:          "danger",
  ouverte:                "info",
  propositions_recues:    "info",
  proposition_acceptee:   "brand",
  presence_confirmee:     "success",
  en_cours:               "brand",
  terminee:               "success",
  validee:                "success",
  annulee:                "neutral",
  litige:                 "danger",
};

export function typeLabel(key) {
  return MISSION_TYPES.find((t) => t.key === key)?.label || key;
}

export function typeIcon(key) {
  return MISSION_TYPES.find((t) => t.key === key)?.icon || "🚗";
}

/* ============================================================
   Money & dates
============================================================ */
export const formatPrice = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(Number(n) || 0);

export const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    }).format(new Date(d));
  } catch (_) { return "—"; }
};

export const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    }).format(new Date(d));
  } catch (_) { return "—"; }
};

/* ============================================================
   API calls
============================================================ */
export async function listMissions(params = {}) {
  const { data } = await API.get("/api/missions", { params });
  return data;
}

export async function getMission(id) {
  const { data } = await API.get(`/api/missions/${id}`);
  return data;
}

export async function getDashboardStats() {
  const { data } = await API.get("/api/missions/stats");
  return data;
}

export async function getProviderSnapshot() {
  const { data } = await API.get("/api/missions/provider-snapshot");
  return data;
}

export async function listMyMissions(as = "created") {
  const { data } = await API.get("/api/missions/mine", { params: { as } });
  return data;
}

export async function listMyProposals() {
  const { data } = await API.get("/api/missions/proposals/mine");
  return data;
}

export async function createMission(payload, photoFiles = []) {
  const fd = new FormData();
  Object.entries(payload || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (Array.isArray(v) || typeof v === "object") {
      fd.append(k, JSON.stringify(v));
    } else {
      fd.append(k, v);
    }
  });
  (photoFiles || []).forEach((f) => fd.append("photos", f));
  const { data } = await API.post("/api/missions", fd);
  return data;
}

export async function updateMission(id, payload, photoFiles = []) {
  const fd = new FormData();
  Object.entries(payload || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (Array.isArray(v) || typeof v === "object") {
      fd.append(k, JSON.stringify(v));
    } else {
      fd.append(k, v);
    }
  });
  (photoFiles || []).forEach((f) => fd.append("photos", f));
  const { data } = await API.put(`/api/missions/${id}`, fd);
  return data;
}

export async function deleteMission(id) {
  const { data } = await API.delete(`/api/missions/${id}`);
  return data;
}

export async function uploadMissionPhotos(id, files, phase = "during") {
  const fd = new FormData();
  fd.append("phase", phase);
  (files || []).forEach((f) => fd.append("photos", f));
  const { data } = await API.post(`/api/missions/${id}/photos`, fd);
  return data;
}

export async function setMissionStatus(id, status, reason = "") {
  const { data } = await API.put(`/api/missions/${id}/status`, { status, reason });
  return data;
}

export async function confirmMissionPresence(id) {
  const { data } = await API.put(`/api/missions/${id}/confirm-presence`);
  return data;
}

export async function saveConvoyingStep(id, phase, payload) {
  const { data } = await API.put(`/api/missions/${id}/convoying`, { phase, ...payload });
  return data;
}

export async function listMissionProposals(id) {
  const { data } = await API.get(`/api/missions/${id}/proposals`);
  return data;
}

export async function createProposal(missionId, payload) {
  const { data } = await API.post(`/api/missions/${missionId}/proposals`, payload);
  return data;
}

export async function acceptProposal(proposalId, payload = {}) {
  const { data } = await API.put(`/api/missions/proposals/${proposalId}/accept`, payload);
  return data;
}

export async function refuseProposal(proposalId) {
  const { data } = await API.put(`/api/missions/proposals/${proposalId}/refuse`);
  return data;
}

export async function withdrawProposal(proposalId) {
  const { data } = await API.put(`/api/missions/proposals/${proposalId}/withdraw`);
  return data;
}

export async function listMessages(missionId) {
  const { data } = await API.get(`/api/missions/${missionId}/messages`);
  return data;
}

export async function sendMessage(missionId, { text = "", attachments = [], lng, lat, label }) {
  const fd = new FormData();
  if (text) fd.append("text", text);
  if (lng !== undefined && lat !== undefined) {
    fd.append("lng", lng);
    fd.append("lat", lat);
    if (label) fd.append("label", label);
  }
  (attachments || []).forEach((f) => fd.append("attachments", f));
  const { data } = await API.post(`/api/missions/${missionId}/messages`, fd);
  return data;
}

export async function listAdminMissions(params = {}) {
  const { data } = await API.get("/api/admin/missions", { params });
  return data;
}

export async function reviewAdminMission(id, payload) {
  const { data } = await API.put(`/api/admin/missions/${id}/review`, payload);
  return data;
}

export async function listAdminMissionProposals(params = {}) {
  const { data } = await API.get("/api/admin/mission-proposals", { params });
  return data;
}
